import React, {useMemo} from 'react';

import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {
    CertificationVoucherBundleUnit,
    type CertificationBundle,
} from '@udemy/react-certification-preparation';
import {CourseUnit, CourseUnitSkeleton} from '@udemy/react-discovery-units';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';

import {useCourses, CourseProvider} from 'src/hooks/certification_vouchers/useCourses';
import {SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT} from 'src/lib/experimentation-constants';

import {SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS} from '../../../topic/constants';
import styles from './course-units.module.less';

export interface CourseUnitsProps {
    provider?: CourseProvider;
    certificationBundle?: CertificationBundle | null;
}

export const CourseUnits = ({provider = 'all', certificationBundle}: CourseUnitsProps) => {
    const {gettext} = useI18n();
    const isMobileMax = useMatchMedia('mobile-max');
    const {prepCourses, practiceCourses, isLoading: isCoursesLoading} = useCourses(provider);
    const {isLoading: isFeaturesLoading, featureVariantAssignments} = useFeatures([
        SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT,
    ]);

    const certificationVoucherBundleUnitFeature = featureVariantAssignments.get(
        SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT,
    );
    const isCertificationVoucherBundleUnitFeatureEnabled =
        certificationVoucherBundleUnitFeature?.configuration?.showCertificationVoucherBundleUnit ??
        false;

    const isLoading = useMemo(
        () => isCoursesLoading || isFeaturesLoading,
        [isCoursesLoading, isFeaturesLoading],
    );

    const courseUnitProps = {
        type: 'course_unit',
        title: gettext('Prep for your exam with courses from real-world experts'),
        items: prepCourses,
        remaining_item_count: 1,
    };

    const practiceCourseUnitProps = {
        ...courseUnitProps,
        title: gettext('Test your knowledge with a practice test before the real thing'),
        items: practiceCourses,
    };

    const courseCardProps = {
        showCourseTag: false,
        showBadges: true,
        showAddToCartInCourseCard: false,
    };

    const unitProps = {
        showTitle: true,
        showPager: true,
        gridCols: 4,
        showQuickViewBox: false,
        showStateAwareCtaInCourseCard: !isMobileMax,
    };

    const hasPrepCourses = prepCourses.length > 0;
    const hasPracticeCourses = practiceCourses.length > 0;

    const shouldShowVoucherBundleUnit =
        isCertificationVoucherBundleUnitFeatureEnabled && certificationBundle;

    if (!isLoading && !hasPrepCourses && !hasPracticeCourses && !shouldShowVoucherBundleUnit) {
        return null;
    }

    return (
        <div className={styles['course-units-container']}>
            {isLoading ? (
                <CourseUnitSkeleton
                    layout="singlerow"
                    cardCount={4}
                    className={styles['skeleton-fill']}
                />
            ) : (
                <>
                    {hasPrepCourses && (
                        <CourseUnit
                            {...unitProps}
                            layout="singlerow"
                            unit={courseUnitProps}
                            courseCardProps={courseCardProps}
                            className={styles['course-unit-container']}
                            allowAddToCartSuccessModal={false}
                            carouselProps={{
                                ariaLabel: courseUnitProps.title,
                                ariaRoleDescription: gettext('carousel'),
                            }}
                        />
                    )}

                    {certificationBundle && !isFeaturesLoading && (
                        <TrackImpression
                            trackFunc={() => {
                                if (certificationVoucherBundleUnitFeature) {
                                    const {experimentIds = [], isLocalDefault = false} =
                                        certificationVoucherBundleUnitFeature;
                                    Tracker.publishEvent(
                                        new ExperimentImpressionEvent(
                                            experimentIds,
                                            isLocalDefault,
                                        ),
                                    );
                                }
                            }}
                        >
                            <div />
                        </TrackImpression>
                    )}

                    {shouldShowVoucherBundleUnit && (
                        <div className={styles['course-unit-container']}>
                            <CertificationVoucherBundleUnit
                                bundle={certificationBundle}
                                {...SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS}
                            />
                        </div>
                    )}

                    {hasPracticeCourses && (
                        <CourseUnit
                            {...unitProps}
                            layout="singlerow"
                            unit={practiceCourseUnitProps}
                            courseCardProps={courseCardProps}
                            allowAddToCartSuccessModal={false}
                            carouselProps={{
                                ariaLabel: practiceCourseUnitProps.title,
                                ariaRoleDescription: gettext('carousel'),
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
};
